import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, X, ChevronDown, ChevronRight, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import { toast } from "sonner";

/**
 * Amber banner on the Production page listing bread packed on previous
 * shift-days that either still has `remaining_packs` on the shelf OR was
 * explicitly logged as unsold (donated/binned/given to staff).
 *
 * Dismissible per session — the state lives in the parent (Production.jsx)
 * so it survives tab-switches between the Batches / Create / History views.
 *
 * Admins get a **Clear** button that wipes remaining/unsold on every pre-
 * today packing row via POST /packing/carry-over/clear (gated by supervisor
 * PIN). Traceability data (produced/waste totals) is preserved.
 */
export function CarryOverPanel({ dismissed, onDismiss, locationId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = locationId ? { location_id: locationId } : {};
        const { data: res } = await api.get("/packing/carry-over", { params });
        if (!cancelled) setData(res);
      } catch (_err) {
        if (!cancelled) setData({ products: [], totals: {} });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [locationId, reloadTick]);

  const doClear = async () => {
    try {
      const params = locationId ? { location_id: locationId } : {};
      const { data: res } = await api.post("/packing/carry-over/clear", null, {
        params,
      });
      toast.success(
        `Carry-over cleared · ${res.cleared} batch${res.cleared === 1 ? "" : "es"} zeroed`,
      );
      setReloadTick((n) => n + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    }
  };

  if (dismissed || !data || (data.products?.length ?? 0) === 0) return null;

  const total = (data.totals?.remaining_packs || 0) + (data.totals?.unsold_packs || 0);

  return (
    <div
      className="rounded-xl border border-amber-300/70 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-700/50 p-4 mb-4"
      data-testid="carry-over-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-start gap-3 flex-1 text-left"
          data-testid="carry-over-toggle"
        >
          <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-amber-900 dark:text-amber-100">
              Carry-over from previous days
            </div>
            <div className="text-sm text-amber-800/80 dark:text-amber-200/80 mt-0.5">
              {data.totals.products} product{data.totals.products === 1 ? "" : "s"} ·{" "}
              <span className="font-bold" data-testid="carry-over-total">{total}</span> pack{total === 1 ? "" : "s"} still on hand
              {" · "}subtract from today&apos;s plan
              <span className="ml-2 text-xs">
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5 inline" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 inline" />
                )}
              </span>
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPinOpen(true)}
              data-testid="carry-over-clear"
              className="h-8 gap-1.5 border-amber-400 text-amber-900 hover:bg-amber-100"
              title="Zero all pre-today carry-over (admin PIN)"
            >
              <Eraser className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={onDismiss}
            data-testid="carry-over-dismiss"
            title="Dismiss for this session"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 overflow-x-auto">
          <table
            className="w-full min-w-[560px] text-sm"
            data-testid="carry-over-table"
          >
            <thead className="text-left text-xs uppercase tracking-wider text-amber-900/60 dark:text-amber-200/60">
              <tr>
                <th className="py-2">Product</th>
                <th className="text-right pr-4">On hand</th>
                <th className="text-right pr-4">Unsold logged</th>
                <th className="text-right">Batches</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p) => (
                <tr
                  key={p.recipe_id}
                  className="border-t border-amber-200/60 dark:border-amber-800/40"
                  data-testid={`carry-over-row-${p.recipe_id}`}
                >
                  <td className="py-2 font-medium text-amber-900 dark:text-amber-100">
                    {p.recipe_name}
                  </td>
                  <td className="text-right pr-4 tnum font-bold">
                    {p.remaining_packs}
                  </td>
                  <td className="text-right pr-4 tnum">{p.unsold_packs || "—"}</td>
                  <td className="text-right tnum text-amber-800/70">
                    {p.packing_rows?.length ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SupervisorPinModal
        open={pinOpen}
        title="Clear production carry-over"
        description="This zeros remaining and unsold packs on every batch packed before today. Reports keep the historic produced/waste totals."
        roles={["admin"]}
        onClose={() => setPinOpen(false)}
        onVerified={() => {
          setPinOpen(false);
          doClear();
        }}
      />
    </div>
  );
}
